import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  BookMarked,
  BookOpen,
  BrainCircuit,
  Building2,
  ChartNoAxesCombined,
  ChevronDown,
  Circle,
  Fingerprint,
  FileText,
  FolderSync,
  Info,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MessageSquare,
  MessagesSquare,
  Network,
  Route,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Tags,
  TrendingDown,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react';
import { cn } from '@tokenforge/design-system';
import { useAuth } from '../auth/useAuth';
import { ROLE_LABELS } from '../auth/roles';
import { logout } from '../auth/logout';
import { useFeatureFlagContext } from '../feature-flags/FeatureFlagProvider';
import {
  buildMoreManagementBlocks,
  buildVisibleNavigation,
  findActiveNavigationItem,
  type NavigationItem,
  type VisibleNavigation,
} from './navigation';

interface Props {
  onNavigate?: () => void;
  className?: string;
  navigation?: VisibleNavigation;
}

interface NavigationRowProps {
  item: NavigationItem;
  secondaryItems: NavigationItem[];
  activeId: string | undefined;
  onNavigate?: () => void;
}

export const NAVIGATION_ICONS: Record<
  string,
  ComponentType<{ size?: number; 'aria-hidden'?: boolean }>
> = {
  dashboard: LayoutDashboard,
  'enterprise-api-keys': KeyRound,
  'my-api-keys': KeyRound,
  'token-sources': Network,
  routing: Route,
  'model-inventory': BookOpen,
  'my-models': BookOpen,
  usage: BarChart3,
  'my-usage': BarChart3,
  leaderboard: BarChart3,
  attribution: ChartNoAxesCombined,
  reports: FileText,
  conversations: MessageSquare,
  wiki: BookMarked,
  'conversation-classify': Tags,
  'knowledge-graph': BrainCircuit,
  'rag-savings': TrendingDown,
  audit: ShieldCheck,
  dlp: ShieldAlert,
  status: Activity,
  onboarding: ListChecks,
  members: Users,
  departments: Building2,
  'self-registration': UserPlus,
  directory: FolderSync,
  oidc: Fingerprint,
  'org-settings': Settings2,
  about: Info,
  diagnostic: Wrench,
  chat: MessagesSquare,
  docs: BookOpen,
};

function NavigationLink({
  item,
  active,
  onNavigate,
  secondary,
}: {
  item: NavigationItem;
  active: boolean;
  onNavigate?: () => void;
  secondary?: boolean;
}) {
  const Icon = NAVIGATION_ICONS[item.id] ?? Circle;
  const className = cn(
    'console-nav-link',
    secondary && 'console-nav-link-secondary',
    active && 'is-active',
  );
  const content = (
    <>
      <Icon size={16} aria-hidden />
      <span>{item.label}</span>
    </>
  );

  if (item.external) {
    return (
      <a className={className} href={item.to} onClick={onNavigate} rel="noreferrer" target="_blank">
        {content}
      </a>
    );
  }

  return (
    <NavLink
      aria-current={active ? 'page' : undefined}
      className={className}
      onClick={onNavigate}
      to={item.to}
    >
      {content}
    </NavLink>
  );
}

function NavigationRow({ item, secondaryItems, activeId, onNavigate }: NavigationRowProps) {
  const hasSecondaryItems = secondaryItems.length > 0;
  const hasActiveSecondary = secondaryItems.some((child) => child.id === activeId);
  const [expanded, setExpanded] = useState(() => hasActiveSecondary);

  useEffect(() => {
    if (hasActiveSecondary) setExpanded(true);
  }, [hasActiveSecondary]);

  return (
    <div className="console-nav-row">
      <div className="console-nav-row-main">
        <NavigationLink item={item} active={item.id === activeId} onNavigate={onNavigate} />
        {hasSecondaryItems && (
          <button
            aria-expanded={expanded}
            aria-label={`展开${item.label}子菜单`}
            className="console-nav-expand"
            onClick={() => setExpanded((current) => !current)}
            type="button"
          >
            <ChevronDown aria-hidden size={16} />
          </button>
        )}
      </div>
      {hasSecondaryItems && expanded && (
        <div className="console-nav-secondary">
          {secondaryItems.map((child) => (
            <NavigationLink
              key={child.id}
              active={child.id === activeId}
              item={child}
              onNavigate={onNavigate}
              secondary
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MoreManagement({
  items,
  secondaryByParent,
  activeId,
  onNavigate,
}: {
  items: NavigationItem[];
  secondaryByParent: Record<string, NavigationItem[]>;
  activeId: string | undefined;
  onNavigate?: () => void;
}) {
  if (items.length === 0) return null;
  const blocks = buildMoreManagementBlocks(items);

  return (
    <section aria-label="更多管理" className="console-nav-more">
      <h2>更多管理</h2>
      {blocks.map((block) =>
        block.kind === 'item' ? (
          <NavigationRow
            activeId={activeId}
            item={block.item}
            key={block.key}
            onNavigate={onNavigate}
            secondaryItems={secondaryByParent[block.item.id] ?? []}
          />
        ) : (
          <section aria-label={block.label} className="console-nav-more-group" key={block.key}>
            <h3>{block.label}</h3>
            {block.items.map((item) => (
              <NavigationRow
                activeId={activeId}
                item={item}
                key={item.id}
                onNavigate={onNavigate}
                secondaryItems={secondaryByParent[item.id] ?? []}
              />
            ))}
          </section>
        ),
      )}
    </section>
  );
}

export function WorkspaceSideNav({ onNavigate, className, navigation: suppliedNavigation }: Props) {
  const { role, user, tenant } = useAuth();
  const { flags } = useFeatureFlagContext();
  const location = useLocation();

  if (!role) return null;

  const navigation = suppliedNavigation ?? buildVisibleNavigation(role, flags);
  const active = findActiveNavigationItem(location.pathname, navigation.searchable);
  const displayName = user?.name?.trim() || user?.email || '已登录';
  const isSaasInternalRoute =
    location.pathname.startsWith('/saas') ||
    location.pathname.startsWith('/saas-login') ||
    location.pathname.startsWith('/internal/tenant-debug');

  return (
    <aside className={cn('console-sidebar', className)}>
      <div className="console-workspace">
        <span>工作区</span>
        <strong>{tenant?.name?.trim() || '当前企业'}</strong>
      </div>
      <div className="console-sidebar-scroll">
        <nav aria-label="工作区导航" className="console-nav-primary">
          {navigation.primary.map((item) => (
            <NavigationRow
              activeId={active?.id}
              item={item}
              key={item.id}
              onNavigate={onNavigate}
              secondaryItems={navigation.secondaryByParent[item.id] ?? []}
            />
          ))}
        </nav>
        <MoreManagement
          activeId={active?.id}
          items={navigation.more}
          onNavigate={onNavigate}
          secondaryByParent={navigation.secondaryByParent}
        />
      </div>
      <div className="console-sidebar-user">
        <div>
          <strong>{displayName}</strong>
          <span>{ROLE_LABELS[role]}</span>
        </div>
        <button
          aria-label="退出登录"
          onClick={() => void logout(isSaasInternalRoute)}
          type="button"
        >
          <LogOut aria-hidden size={16} />
          <span>退出</span>
        </button>
      </div>
    </aside>
  );
}
